import React, { useState } from 'react';
import styled from 'styled-components';
import * as api from '../utils/api';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Modal = styled.div`
  background: ${props => props.$darkMode ? '#161b22' : '#fff'};
  border-radius: 12px;
  padding: 32px;
  width: 500px;
  max-width: 90vw;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
`;

const Title = styled.h2`
  margin: 0 0 24px 0;
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  font-size: 24px;
  font-weight: 600;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  font-weight: 600;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  background: ${props => props.$darkMode ? '#0d1117' : '#fff'};
  border: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  border-radius: 6px;
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  font-size: 14px;
  margin-bottom: 16px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #0969da;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  background: ${props => props.$darkMode ? '#0d1117' : '#fff'};
  border: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  border-radius: 6px;
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  font-size: 14px;
  margin-bottom: 16px;
  min-height: 80px;
  resize: vertical;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #0969da;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.$primary ? `
    background: #0969da;
    color: #fff;
    &:hover { background: #0860ca; }
  ` : `
    background: ${props.$darkMode ? '#21262d' : '#f6f8fa'};
    color: ${props.$darkMode ? '#e6edf3' : '#24292f'};
    border: 1px solid ${props.$darkMode ? '#30363d' : '#d0d7de'};
    &:hover { background: ${props.$darkMode ? '#30363d' : '#eaeef2'}; }
  `}
`;

function EditMetadataModal({ specId, darkMode, onClose, onSave }) {
  const [specName, setSpecName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    api.getSpecs().then(specs => {
      const spec = specs.find(s => s.id === specId);
      if (spec) {
        setSpecName(spec.spec_name || '');
        setTitle(spec.title || '');
        setDescription(spec.description || '');
        setCategory(spec.category || '');
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [specId]);

  const handleSave = async () => {
    if (!specName || !title) {
      alert('Spec name and title are required');
      return;
    }

    setSaving(true);
    try {
      await api.updateSpec(specId, { spec_name: specName, title, description, category });
      onSave({ spec_name: specName, title, description, category });
      onClose();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Overlay>
        <Modal $darkMode={darkMode}>
          <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
        </Modal>
      </Overlay>
    );
  }

  return (
    <Overlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <Modal $darkMode={darkMode}>
        <Title $darkMode={darkMode}>Edit Metadata</Title>
        
        <Label $darkMode={darkMode}>Spec Name *</Label>
        <Input 
          $darkMode={darkMode}
          value={specName}
          onChange={(e) => setSpecName(e.target.value)}
          placeholder="my-api-spec"
        />
        
        <Label $darkMode={darkMode}>Title *</Label>
        <Input 
          $darkMode={darkMode}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="My API"
        />
        
        <Label $darkMode={darkMode}>Description</Label>
        <Textarea 
          $darkMode={darkMode}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="API description..."
        />
        
        <Label $darkMode={darkMode}>Category</Label>
        <Input 
          $darkMode={darkMode}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="REST API"
        />
        
        <ButtonGroup>
          <Button $darkMode={darkMode} onClick={onClose}>Cancel</Button>
          <Button $darkMode={darkMode} $primary onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </ButtonGroup>
      </Modal>
    </Overlay>
  );
}

export default EditMetadataModal;
